import React, { useEffect, useRef, useState } from "react";
import { ulid } from "ulid";
import "./CapitalChangeDialog.scss";
import { PersonProjectI } from "../../../../../../api/request/Lead/Interfaces/PersonProjectInterface";
import {
  CapitalChangeAnyPriceI,
  CapitalChangeLimitsI,
  CapitalChangePreviewI,
  CapitalChangePriceI,
  CapitalChangeQuotePriceI,
  CapitalChangeResultI,
} from "../../../../../../api/request/Lead/Interfaces/CapitalChangeInterface";
import {
  capitalChangeBlockerText,
  capitalChangeError,
  capitalChangeErrorText,
  CapitalChangeTranslateT,
  DEFAULT_CAPITAL_LIMITS,
  isCapitalOnGrid,
  isQuoteStagePrice,
  snapCapital,
  wholeEuros,
} from "../../../../../../native/capitalChange";

const BRAND = "var(--primary-color, #423EF4)";
const BRAND_SOFT = "var(--light-primary-color, #EEF0FF)";

export interface CapitalChangeDialogI {
  project: PersonProjectI;
  /** «Capitale 230.000 € · Decesso»: qué preventivo es. */
  detail: string;
  onPreview: (project: PersonProjectI, capital?: number) => Promise<CapitalChangePreviewI>;
  onConfirm: (
    project: PersonProjectI,
    capital: number,
    operationId: string
  ) => Promise<CapitalChangeResultI>;
  onClose: () => void;
  onChanged: (result: CapitalChangeResultI) => void;
  t: CapitalChangeTranslateT;
  /** Inyectable en los tests. */
  newOperationId?: () => string;
  debounceMs?: number;
}

type LoadT = "loading" | "ready" | "failed";

const money = (amount: number, decimals: number) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);

const euros = wholeEuros;
const premium = (amount: number) => money(amount, 2);

/** 16/11/2026 a partir de 2026-11-16 (sin pasar por Date: es un día, no un instante). */
const dayOf = (iso: string): string => {
  const [year, month, day] = iso.slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : iso;
};

/** «200.000», «200000» o «200 000 €» → 200000; null si no es un número. */
const parseCapital = (raw: string): number | null => {
  const digits = raw.replace(/[^\d]/g, "");
  if ("" === digits) return null;
  return parseInt(digits, 10);
};

/**
 * «Modifica capitale» de un preventivo del Portal Cliente (04-nov-2026). Hasta la
 * nota legal el cliente elige otro capital de la rejilla, ve la prima exacta que
 * pagará (la calcula ms-agl con el mismo calendario) y la confirma. Al abrir pide
 * si se puede y lo vigente; cada capital nuevo se tarifica con un debounce y las
 * respuestas viejas se descartan.
 *
 * Sin oferta elegida (etapa QUOTE, v1.1) no hay periodicidad ni decorrenza: se
 * enseñan la tarifa mensual y la anual (las de la pantalla de la oferta) y, al
 * confirmar, el cliente las elige continuando el preventivo.
 */
const CapitalChangeDialog = ({
  project,
  detail,
  onPreview,
  onConfirm,
  onClose,
  onChanged,
  t,
  newOperationId = ulid,
  debounceMs = 400,
}: CapitalChangeDialogI) => {
  const [load, setLoad] = useState<LoadT>("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [base, setBase] = useState<CapitalChangePreviewI | null>(null);
  const [capital, setCapital] = useState<number>(project.capital ?? DEFAULT_CAPITAL_LIMITS.min);
  const [input, setInput] = useState<string>(String(project.capital ?? ""));
  const [quote, setQuote] = useState<CapitalChangeAnyPriceI | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Un mismo operationId para los reintentos del mismo capital (idempotente); uno
  // nuevo si el cliente cambia de capital tras un intento.
  // Se genera una sola vez al abrir (un useRef con valor inicial lo evaluaría en
  // cada render).
  const operation = useRef<{ id: string; capital: number | null } | null>(null);
  if (null === operation.current) {
    operation.current = { id: newOperationId(), capital: null };
  }
  const previewSeq = useRef(0);

  const limits: CapitalChangeLimitsI = base?.limits ?? DEFAULT_CAPITAL_LIMITS;
  const current = base?.current ?? null;
  const currentCapital = current?.capital ?? project.capital ?? null;
  const inputValid = isCapitalOnGrid(capital, limits) && parseCapital(input) === capital;
  const isCurrent = null !== currentCapital && capital === currentCapital;
  const changeable = "ready" === load && true === base?.changeable && 0 === blockers.length;
  // Sin oferta elegida: lo dice ms-agl o el precio vigente sin periodicidad.
  const quoteStage =
    "QUOTE" === base?.stage || (null !== current && isQuoteStagePrice(current));

  // Al abrir: si se puede y lo vigente.
  useEffect(() => {
    let alive = true;
    setLoad("loading");
    setLoadError(null);
    onPreview(project)
      .then((preview) => {
        if (!alive) return;
        setBase(preview);
        setBlockers(preview.changeable ? [] : preview.blockers ?? []);
        const start = preview.current?.capital ?? project.capital;
        if (null !== start && undefined !== start) {
          setCapital(start);
          setInput(String(start));
        }
        setLoad("ready");
      })
      .catch((error) => {
        if (!alive) return;
        const kind = capitalChangeError(error);
        if ("REFUSED" === kind.kind) {
          setBlockers(0 < kind.blockers.length ? kind.blockers : [""]);
          setLoad("ready");
          return;
        }
        setLoadError(capitalChangeErrorText(kind, DEFAULT_CAPITAL_LIMITS, t, euros, "preview"));
        setLoad("failed");
      });
    return () => {
      alive = false;
    };
    // `attempt` reintenta la carga; el resto no cambia mientras está abierto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // Cada capital nuevo y válido se tarifica con debounce; solo vale la última respuesta.
  useEffect(() => {
    const seq = ++previewSeq.current;
    setQuote(null);
    setPreviewError(null);
    if (!changeable || !inputValid || isCurrent) {
      setPreviewing(false);
      return;
    }
    setPreviewing(true);
    const timer = setTimeout(() => {
      onPreview(project, capital)
        .then((preview) => {
          if (seq !== previewSeq.current) return;
          if (!preview.changeable) {
            setBlockers(0 < (preview.blockers ?? []).length ? preview.blockers : [""]);
            return;
          }
          if (null === preview.quote || preview.quote.capital !== capital) {
            setPreviewError(
              capitalChangeErrorText({ kind: "UNKNOWN" }, limits, t, euros, "preview")
            );
            return;
          }
          setQuote(preview.quote);
        })
        .catch((error) => {
          if (seq !== previewSeq.current) return;
          const kind = capitalChangeError(error);
          if ("REFUSED" === kind.kind) {
            setBlockers(0 < kind.blockers.length ? kind.blockers : [""]);
            return;
          }
          setPreviewError(capitalChangeErrorText(kind, limits, t, euros, "preview"));
        })
        .finally(() => {
          if (seq === previewSeq.current) setPreviewing(false);
        });
    }, debounceMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capital, inputValid, changeable, isCurrent]);

  const close = () => {
    if (confirmBusy) return;
    onClose();
  };

  const choose = (value: number) => {
    setCapital(value);
    setInput(String(value));
    setConfirmError(null);
  };

  const onTyped = (raw: string) => {
    setInput(raw);
    setConfirmError(null);
    const parsed = parseCapital(raw);
    if (null !== parsed) setCapital(parsed);
  };

  // Al salir del campo, se ajusta a la rejilla (como el slider).
  const onTypedBlur = () => {
    const parsed = parseCapital(input);
    choose(snapCapital(null === parsed ? currentCapital ?? limits.min : parsed, limits));
  };

  const canConfirm =
    changeable &&
    inputValid &&
    !isCurrent &&
    null !== quote &&
    quote.capital === capital &&
    !previewing &&
    !confirmBusy;

  const confirm = async () => {
    if (!canConfirm) return;
    let op = operation.current ?? { id: newOperationId(), capital: null };
    if (null !== op.capital && op.capital !== capital) {
      op = { id: newOperationId(), capital: null };
    }
    op.capital = capital;
    operation.current = op;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const result = await onConfirm(project, capital, op.id);
      onChanged(result);
    } catch (error) {
      const kind = capitalChangeError(error);
      if ("REFUSED" === kind.kind) {
        setBlockers(0 < kind.blockers.length ? kind.blockers : [""]);
      } else {
        setConfirmError(capitalChangeErrorText(kind, limits, t, euros, "confirm"));
      }
    } finally {
      setConfirmBusy(false);
    }
  };

  const perYear = t("newDashboard.quotes.capitalChange.perYear", "all'anno");
  const perMonth = t("newDashboard.quotes.capitalChange.perMonth", "al mese");
  const suffix = (price: CapitalChangePriceI) =>
    "YEARLY" === price.frequency ? perYear : perMonth;

  /** «da 41,40 € al mese · 477,90 € all'anno»: lo vigente sin periodicidad elegida. */
  const quoteStageSummary = (price: CapitalChangeQuotePriceI): string | null => {
    const parts = [
      null === price.monthly ? null : `${premium(price.monthly.firstPayment)} ${perMonth}`,
      null === price.yearly ? null : `${premium(price.yearly.yearlyTotal)} ${perYear}`,
    ].filter((part): part is string => null !== part);
    if (0 === parts.length) return null;
    return `${t("newDashboard.quotes.capitalChange.from", "da")} ${parts.join(" · ")}`;
  };

  const pct =
    limits.max > limits.min
      ? ((Math.min(limits.max, Math.max(limits.min, capital)) - limits.min) /
          (limits.max - limits.min)) *
        100
      : 0;

  const hasDiscount = (price: CapitalChangePriceI) =>
    0 < price.discountBps &&
    "number" === typeof price.regularFirstPayment &&
    price.regularFirstPayment > price.firstPayment;

  /** La tarifa sin descuento, si la hay y es mayor que la que se paga. */
  const regularOf = (paid: number, regular: number | undefined, discountBps: number) =>
    0 < discountBps && "number" === typeof regular && regular > paid ? regular : null;

  const regularLabel = t("newDashboard.quotes.capitalChange.regular", "Tariffa senza sconto");

  /** Nuevo premio sin oferta elegida: la tarifa mensual y la anual, lado a lado. */
  const renderQuoteStagePrices = (price: CapitalChangeQuotePriceI) => {
    const options = [
      null === price.monthly
        ? null
        : {
            key: "monthly",
            amount: price.monthly.firstPayment,
            regular: regularOf(
              price.monthly.firstPayment,
              price.monthly.regularFirstPayment,
              price.discountBps
            ),
            period: perMonth,
          },
      null === price.yearly
        ? null
        : {
            key: "yearly",
            amount: price.yearly.yearlyTotal,
            regular: regularOf(
              price.yearly.yearlyTotal,
              price.yearly.regularYearlyTotal,
              price.discountBps
            ),
            period: perYear,
          },
    ].filter((o): o is NonNullable<typeof o> => null !== o);
    return (
      <div className="capitalChange__options" data-testid="capital-change-quote-prices">
        {options.map((o) => (
          <div key={o.key} className="capitalChange__option" data-testid={`capital-change-${o.key}`}>
            <p className="capitalChange__optionPrice">
              {premium(o.amount)} <span>{o.period}</span>
            </p>
            {null !== o.regular && (
              <p className="capitalChange__muted">
                {regularLabel} <s>{premium(o.regular)}</s>
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const blocked = "ready" === load && 0 < blockers.length;

  return (
    <div className="quotesPanel__backdrop" onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="capital-change-title"
        aria-describedby="capital-change-text"
        data-testid="capital-change-dialog"
        className="quotesPanel__dialog capitalChange"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => "Escape" === e.key && close()}
      >
        <div>
          <p id="capital-change-title" className="capitalChange__title">
            {t("newDashboard.quotes.capitalChange.title", "Modifica capitale")}
          </p>
          <p id="capital-change-text" className="capitalChange__text">
            {detail}
          </p>
        </div>

        {"loading" === load && (
          <p role="status" className="capitalChange__muted">
            {t("newDashboard.quotes.capitalChange.loading", "Un momento, stiamo controllando il preventivo…")}
          </p>
        )}

        {"failed" === load && (
          <div className="capitalChange__stack">
            <p role="alert" className="capitalChange__error">
              {loadError}
            </p>
            <button
              type="button"
              className="capitalChange__secondary"
              onClick={() => setAttempt((n) => n + 1)}
            >
              {t("newDashboard.quotes.capitalChange.retry", "Riprova")}
            </button>
          </div>
        )}

        {blocked && (
          <div className="capitalChange__blocked" data-testid="capital-change-blockers">
            {blockers.map((b) => (
              <p key={b || "generic"}>{capitalChangeBlockerText(b, t)}</p>
            ))}
          </div>
        )}

        {"ready" === load && !blocked && (
          <>
            {null !== current && isQuoteStagePrice(current) && (
              <dl className="quotesPanel__facts capitalChange__facts">
                <div>
                  <dt>{t("newDashboard.quotes.capitalChange.currentCapital", "Capitale attuale")}</dt>
                  <dd>{euros(current.capital)}</dd>
                </div>
                {null !== quoteStageSummary(current) && (
                  <div>
                    <dt>{t("newDashboard.quotes.capitalChange.currentPremium", "Premio attuale")}</dt>
                    <dd>{quoteStageSummary(current)}</dd>
                  </div>
                )}
              </dl>
            )}

            {null !== current && !isQuoteStagePrice(current) && (
              <dl className="quotesPanel__facts capitalChange__facts">
                <div>
                  <dt>{t("newDashboard.quotes.capitalChange.currentCapital", "Capitale attuale")}</dt>
                  <dd>{euros(current.capital)}</dd>
                </div>
                <div>
                  <dt>{t("newDashboard.quotes.capitalChange.currentPremium", "Premio attuale")}</dt>
                  <dd>{`${premium(current.firstPayment)} ${suffix(current)}`}</dd>
                </div>
                <div>
                  <dt>{t("newDashboard.quotes.capitalChange.effectDate", "Decorrenza")}</dt>
                  <dd>{dayOf(current.effectDate)}</dd>
                </div>
              </dl>
            )}

            <div className="capitalChange__stack">
              <p className="capitalChange__label">
                {t("newDashboard.quotes.capitalChange.newCapital", "Nuovo capitale")}
              </p>
              <div className="capitalChange__value" aria-hidden="true">
                {euros(capital)}
              </div>
              <input
                type="range"
                className="capitalChange__slider"
                min={limits.min}
                max={limits.max}
                step={limits.step}
                value={Math.min(limits.max, Math.max(limits.min, capital))}
                onChange={(e) => choose(parseInt(e.target.value, 10))}
                disabled={confirmBusy}
                aria-label={t("newDashboard.quotes.capitalChange.newCapital", "Nuovo capitale")}
                aria-valuetext={euros(capital)}
                style={{
                  background: `linear-gradient(to right, ${BRAND} 0%, ${BRAND} ${pct}%, ${BRAND_SOFT} ${pct}%, ${BRAND_SOFT} 100%)`,
                }}
              />
              <div className="capitalChange__range">
                <span>{euros(limits.min)}</span>
                <span>{euros(limits.max)}</span>
              </div>
              <label className="capitalChange__field">
                <span>{t("newDashboard.quotes.capitalChange.amount", "Importo in euro")}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={input}
                  onChange={(e) => onTyped(e.target.value)}
                  onBlur={onTypedBlur}
                  disabled={confirmBusy}
                  aria-invalid={!inputValid}
                  aria-describedby="capital-change-hint"
                />
              </label>
              <p
                id="capital-change-hint"
                className={inputValid ? "capitalChange__muted" : "capitalChange__error"}
              >
                {t(
                  "newDashboard.quotes.capitalChange.hint",
                  "Tra {{min}} e {{max}}, a multipli di {{step}}."
                )
                  .replace("{{min}}", euros(limits.min))
                  .replace("{{max}}", euros(limits.max))
                  .replace("{{step}}", euros(limits.step))}
              </p>
            </div>

            <div className="capitalChange__preview" aria-live="polite" data-testid="capital-change-preview">
              {isCurrent && (
                <p className="capitalChange__muted">
                  {t(
                    "newDashboard.quotes.capitalChange.isCurrent",
                    "È il capitale attuale. Scegline un altro per vedere il nuovo premio."
                  )}
                </p>
              )}
              {!isCurrent && inputValid && previewing && (
                <p role="status" className="capitalChange__muted">
                  {t("newDashboard.quotes.capitalChange.calculating", "Calcolo del premio…")}
                </p>
              )}
              {!isCurrent && null !== quote && !previewing && isQuoteStagePrice(quote) && (
                <>
                  <p className="capitalChange__label">
                    {t("newDashboard.quotes.capitalChange.newPremium", "Nuovo premio")}
                  </p>
                  {renderQuoteStagePrices(quote)}
                </>
              )}
              {!isCurrent && null !== quote && !previewing && !isQuoteStagePrice(quote) && (
                <>
                  <p className="capitalChange__label">
                    {t("newDashboard.quotes.capitalChange.newPremium", "Nuovo premio")}
                  </p>
                  <p className="capitalChange__price">
                    {`${premium(quote.firstPayment)} ${suffix(quote)}`}
                  </p>
                  {hasDiscount(quote) && (
                    <p className="capitalChange__muted">
                      {t("newDashboard.quotes.capitalChange.regular", "Tariffa senza sconto")}{" "}
                      <s>{premium(quote.regularFirstPayment as number)}</s>
                    </p>
                  )}
                  {"MONTHLY" === quote.frequency && (
                    <p className="capitalChange__muted">
                      {t("newDashboard.quotes.capitalChange.firstYear", "Totale primo anno")}{" "}
                      <strong>{premium(quote.yearlyTotal)}</strong>
                    </p>
                  )}
                </>
              )}
              {null !== previewError && (
                <p role="alert" className="capitalChange__error">
                  {previewError}
                </p>
              )}
            </div>

            {quoteStage && (
              <p className="capitalChange__muted" data-testid="capital-change-quote-note">
                {t(
                  "newDashboard.quotes.capitalChange.quoteNote",
                  "Periodicità e decorrenza le scegli continuando il preventivo."
                )}
              </p>
            )}
          </>
        )}

        {null !== confirmError && (
          <p role="alert" className="capitalChange__error">
            {confirmError}
          </p>
        )}

        <div className="capitalChange__stack">
          {"ready" === load && !blocked && (
            <button
              type="button"
              className="capitalChange__primary"
              onClick={() => void confirm()}
              disabled={!canConfirm}
              aria-busy={confirmBusy}
            >
              {confirmBusy
                ? t("newDashboard.quotes.capitalChange.saving", "Salvataggio…")
                : t("newDashboard.quotes.capitalChange.confirm", "Conferma nuovo capitale")}
            </button>
          )}
          <button
            type="button"
            autoFocus
            className="capitalChange__secondary"
            onClick={close}
            disabled={confirmBusy}
          >
            {blocked || "failed" === load
              ? t("newDashboard.quotes.capitalChange.close", "Chiudi")
              : t("newDashboard.quotes.capitalChange.cancel", "Annulla")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CapitalChangeDialog;
